/* eslint-disable prettier/prettier */
import {
  useRef,
  useState,
  useMemo,
  useCallback,
  ElementType,
  createElement,
  CSSProperties,
  useEffect,
  useId,
  Fragment,
  ReactNode,
} from "react"
import get from "lodash/get"
import {
  Area,
  Bar,
  CartesianGrid,
  Line,
  ComposedChart as ReComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { H3, SubTitle } from "../../typography"
import {
  AggregationType,
  CHARTS_LOADING_DATA,
  TOTAL_CALCULATED_DATA_KEY,
  UNKNOWN_DATA_KEY,
} from "~/ui-rtk/constants/charts"
import { useResizeObserver } from "~/ui-rtk/utils/resize-observer"
import { formatNumber, startCase } from "~/ui-rtk/utils/format"
import useTailwindTheme from "~/ui-rtk/hooks/tailwind-theme"
import ChartTooltipContent, {
  type TTootlipNumberProps,
} from "./ChartTooltipContent"
import { Card, RichTooltip } from "../../common"
import { InfoSvg } from "../../svg/essentials"
import { Button } from "~/ui-rtk/components/ui/controls"
import {
  BRAND_COLORS_MAP,
  getColorFromPaletteByIdx,
  getLightColor,
} from "~/ui-rtk/utils/chart-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { propertyNamesToLowercase } from "~/ui-rtk/utils/object-utils"

import useDate from "~/ui-rtk/hooks/date"
import useAggregationGroups from "~/ui-rtk/hooks/aggregation-groups"
import VerticalLegend from "../VerticalLegend/VerticalLegend"
import useChartLoadingDataForAnimation from "~/ui-rtk/hooks/chart-loading-animation"
import ChartDataExportButton from "~/ui-rtk/components/ui/controls/ChartDataExportButton"
const dayjs = useDate()

/**
 * NOTE on "bar": rendering ANY <Bar> series switches the recharts category
 * x-axis scale from "point" to "band", which shifts the x positions of
 * line/area series. Mixing "bar" with "line"/"area"/"dashed" graphs in a
 * single chart is therefore NOT supported — callers must use all-bar or
 * all-line/area graph sets per chart.
 */
export type TGraphType = "line" | "area" | "dashed" | "bar"

export type TGraphProps = {
  type?: TGraphType
  unit?: string
  pluralizeUnit?: boolean
  dataKey: string
  suffixKey?: string
  color?: string
  requiredSources?: string[]
  label?: string
  stacked?: boolean
  isBrand?: boolean
  labelAggType?:
    | "sum"
    | "avg"
    | "percent-total"
    | "percent-change"
    | "percent-average"
  numberType?: "default" | "currency"
  yAxisId?: string
  isTotal?: boolean
  totalConfig?: {
    skipMetrics?: string[]
  }
}

const aggFunc = (
  data: Array<{ date: number; [key: string]: number }>,
  dataKey: string,
  aggType:
    | "sum"
    | "avg"
    | "percent-total"
    | "percent-change"
    | "percent-average",
  graphs: TGraphProps[],
  prefixDataKey: string,
  excludeDataKeyFromTotal?: string[],
  yAxisId?: string,
) => {
  const length = data.length
  const sum = ["sum", "avg", "percent-total", "percent-average"].includes(
    aggType,
  )
    ? data.reduce((sum: number, dp) => {
        const metricData = get(dp, `${prefixDataKey}${dataKey}`)
        if (isNaN(metricData)) {
          return sum
        }

        return +metricData + sum
      }, 0)
    : 0

  const excludeDataKeyFromTotalWithPrefix = (excludeDataKeyFromTotal ?? []).map(
    key => `${prefixDataKey}${key}`,
  )

  switch (aggType) {
    case "avg":
      return sum / length
    case "percent-total": {
      const axisIdKeys = graphs
        .filter(graph => !yAxisId || graph.yAxisId === yAxisId)
        .map(graph => `${prefixDataKey}${graph.dataKey}`)

      const axisIdTotal = data.reduce(
        (total: number, dp) =>
          total +
          axisIdKeys.reduce(
            (sum, key) =>
              isNaN(get(dp, dataKey)) ||
              (excludeDataKeyFromTotalWithPrefix ?? []).includes(key)
                ? sum
                : sum + +get(dp, key),
            0,
          ),
        0,
      )
      return (sum / axisIdTotal) * 100
    }
    case "percent-average": {
      return sum / length
    }
    case "percent-change":
      return length > 0 ? (data[length - 1].value / data[0].value - 1) * 100 : 0
    default:
      return sum
  }
}

const getMaxValueByYAxisId = (
  yAxisId: string,
  graphs: TGraphProps[],
  metrics: Record<string, number>[],
) => {
  if (!yAxisId) {
    return 0
  }
  const filteredGraphsDataKeys = graphs
    .filter(({ yAxisId: graphAxis }) => yAxisId === graphAxis)
    .map(({ dataKey }) => dataKey)

  if (!filteredGraphsDataKeys.length) {
    return 0
  }

  const plainNumberArr = metrics.reduce(
    (accumulator: number[], metric: Record<string, number>) => {
      const items = filteredGraphsDataKeys.map(dataKey =>
        [+metric[dataKey], +metric[`compared.${dataKey}`]].filter(num => !!num),
      )
      return accumulator.concat(...items)
    },
    [],
  )

  return (plainNumberArr ?? []).length > 0
    ? Math.max(...plainNumberArr) * 1.01
    : "dataMax"
}

const getMinValueByYAxisId = (
  yAxisId: string,
  graphs: TGraphProps[],
  metrics: Record<string, number>[],
) => {
  if (!yAxisId) {
    return 0
  }
  const filteredGraphsDataKeys = graphs
    .filter(({ yAxisId: graphAxis }) => yAxisId === graphAxis)
    .map(({ dataKey }) => dataKey)

  if (!filteredGraphsDataKeys.length) {
    return 0
  }

  const plainNumberArr = metrics.reduce(
    (accumulator: number[], metric: Record<string, number>) => {
      const items = filteredGraphsDataKeys.map(dataKey =>
        // unlike the max sibling (which filters with `!!num`), zeros and
        // negatives must be kept here — only non-numeric values are dropped
        [+metric[dataKey], +metric[`compared.${dataKey}`]].filter(
          num => !Number.isNaN(num),
        ),
      )
      return accumulator.concat(...items)
    },
    [],
  )

  if (!plainNumberArr.length) {
    return 0
  }

  const min = Math.min(...plainNumberArr)
  // matching 1% headroom to the max side; only extends below zero — a
  // positive min is clamped to 0 by the caller anyway
  return min < 0 ? min * 1.01 : min
}

export type TComposedChartProps = {
  title?: string
  subTitle?: string
  compareMode: boolean
  compareUnit?: string
  comparePluralizeUnit?: boolean
  compareTitle?: string
  dataSources?: string[]
  sources?: Map<
    string,
    {
      name: string
      icon: string
    }
  >
  compareNumberType?: "default" | "currency"
  compareLabelAggType?: "sum" | "avg" | "percent-change"
  dataAggType?: AggregationType
  dataAggTypeOnly?: AggregationType
  metrics: Record<string, unknown>[]
  interactive?: boolean
  graphs: TGraphProps[]
  graphsFromColumn?: {
    column: string
    type: TGraphType
    label: string
    unit?: string
    suffixKey?: string
    pluralizeUnit?: boolean
    labelAggType?:
      | "sum"
      | "avg"
      | "percent-total"
      | "percent-change"
      | "percent-average"
    numberType?: "default" | "currency"
  }
  excludeDataKeyFromTotal?: Array<string>
  totalLabelProps?: TGraphProps
  state?: string
  setDataAggType?: (agg: AggregationType) => void
  isCompareMetricMain?: boolean
  className?: string
  highlightKey?: string
  stacked?: boolean
  manualTotal?: boolean
  tooltipTotalKey?: string
  hideAggSelector?: boolean
  hideTotal?: boolean
  totalComponent?: ElementType
  tooltipProps?: TTootlipNumberProps
  graphsManualSort?: boolean
  description?: string
  emptyStateText?: string
  blurTitle?: boolean
  hideTooltipTitle?: boolean
  isInverted?: boolean
  bottomLegend?: boolean
  tooltipFooter?: ReactNode
  isLoading?: boolean
  enableChartDataExport?: boolean
  chartDataExportFilename?: string
  /**
   * recharts series animation (default on). Callers that resize the chart
   * live (a drag handle) pass false while dragging: recharts bumps its
   * updateId on every width/height change and would replay the 1.5s draw
   * animation each frame.
   */
  isAnimationActive?: boolean
}

export default function ComposedChart({
  title,
  subTitle,
  dataSources: dataSourcesProp,
  dataAggType: dataAggTypeProp,
  dataAggTypeOnly,
  compareMode = false,
  metrics,
  sources,
  interactive = true,
  graphs: graphsProps,
  graphsFromColumn,
  totalLabelProps,
  state = "idle",
  setDataAggType: setDataAggTypeProp = () => null,
  isCompareMetricMain = false,
  excludeDataKeyFromTotal,
  className,
  highlightKey,
  stacked = false,
  hideTotal = false,
  manualTotal = false,
  hideAggSelector = false,
  totalComponent,
  tooltipProps,
  graphsManualSort = false,
  description,
  emptyStateText,
  isInverted = false,
  blurTitle = false,
  hideTooltipTitle = false,
  bottomLegend = false,
  tooltipFooter = null,
  isLoading = false,
  enableChartDataExport = false,
  chartDataExportFilename,
  isAnimationActive = true,
}: TComposedChartProps) {
  const [focusedCharts, setFocusedCharts] = useState<string[]>([])
  const [dataAggType, setDataAggType] = useState(
    dataAggTypeProp ?? AggregationType.DAILY,
  )
  const [tooltipFrozen, setTooltipFrozen] = useState(false)
  const [height, setHeight] = useState(0)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const theme = useTailwindTheme()
  const mainMetricPrefix = isCompareMetricMain ? "compared." : ""
  const comparedMetricPrefix = isCompareMetricMain ? "" : "compared."
  // SVG pattern ids are document-global: two chart instances on one page must
  // not share them, or one chart's compare bars would resolve to the other's
  // <pattern>. useId is unique per instance; strip the ":" wrappers so the id
  // stays a valid url(#...) fragment.
  const comparePatternIdBase = useId().replace(/[^a-zA-Z0-9_-]/g, "")
  const getComparePatternId = (graphIdx: number) =>
    `composed-chart-compare-${comparePatternIdBase}-${graphIdx}`

  const normalizedMetrics = (metrics ?? []).map(
    propertyNamesToLowercase,
  ) as Record<string, unknown>[]
  const typedMetrics = normalizedMetrics as Array<{
    date: number
    [key: string]: number
  }>
  const normalizedHighlightKey = highlightKey?.toLowerCase()

  const refEl = useRef<HTMLElement>(document.body)
  const size = useResizeObserver({ ref: refEl })
  const chartWidth = (size.width || refEl.current?.clientWidth) ?? 0
  const dataSources: string[] =
    dataSourcesProp ??
    (normalizedMetrics?.length > 0 &&
    (
      (normalizedMetrics?.[0] as Record<string, unknown>)?.metadata as Record<
        string,
        unknown
      >
    )?.source
      ? Array.from(
          (
            (normalizedMetrics?.[0] as Record<string, unknown>)
              .metadata as Record<string, unknown>
          ).source as Iterable<string>,
        )
      : [])

  let prevTick: string | number | null = null
  const formatTick = (value: string | number, index: number) => {
    const date = dayjs(value)
    const tick = date.format(
      date.month() !== dayjs(prevTick).month() || index === 0 ? `DD MMM` : "DD",
    )
    prevTick = value
    return tick
  }

  useEffect(() => {
    setHeight(wrapperRef.current?.clientHeight ?? 0)
  }, [])

  const isMetricHighlighted = (dataKey: string) => {
    if (!normalizedHighlightKey) {
      return false
    }
    return normalizedHighlightKey === dataKey.toLowerCase()
  }

  const graphsRaw = useMemo(() => {
    if (graphsProps) {
      return graphsProps
    } else if (!graphsFromColumn) {
      throw new Error("graphs or graphsFromColumn must be defined")
    } else if (!normalizedMetrics?.length) {
      return []
    }

    return Object.keys(normalizedMetrics[0][graphsFromColumn?.column] ?? {})
      .map<TGraphProps>(key => ({
        type: graphsFromColumn?.type ?? "line",
        dataKey: graphsFromColumn?.suffixKey
          ? `${graphsFromColumn?.column}.${key}.${graphsFromColumn.suffixKey}`
          : `${graphsFromColumn?.column}.${key}`,
        label: startCase(key),
        numberType: graphsFromColumn?.numberType ?? "default",
        unit: graphsFromColumn?.unit,
        pluralizeUnit: graphsFromColumn?.pluralizeUnit ?? true,
        labelAggType: graphsFromColumn?.labelAggType ?? "sum",
      }))
      .concat(
        Object.keys(
          (normalizedMetrics[0][UNKNOWN_DATA_KEY] as Record<string, unknown>)[
            graphsFromColumn?.column
          ] as Record<string, unknown>,
        ).map<TGraphProps>(key => ({
          type: graphsFromColumn?.type ?? "line",
          dataKey: graphsFromColumn?.suffixKey
            ? `${UNKNOWN_DATA_KEY}.${graphsFromColumn?.column}.${key}.${graphsFromColumn?.suffixKey}`
            : `${UNKNOWN_DATA_KEY}.${graphsFromColumn?.column}.${key}`,
          label: `Unknown: ${startCase(key)}`,
          numberType: graphsFromColumn?.numberType ?? "default",
          unit: graphsFromColumn?.unit,
          pluralizeUnit: graphsFromColumn?.pluralizeUnit ?? true,
          color: BRAND_COLORS_MAP.UNKNOWN,
          labelAggType: graphsFromColumn?.labelAggType ?? "sum",
        })),
      )
  }, [graphsProps, graphsFromColumn, normalizedMetrics])

  const graphs = useMemo(
    () =>
      graphsRaw.map(graph => ({
        ...graph,
        dataKey: graph.dataKey.toLowerCase(),
      })),
    [graphsRaw],
  )

  const aggData = useMemo(
    () =>
      graphs
        .filter(
          ({ requiredSources }) =>
            !focusedCharts.length ||
            focusedCharts.some(source => requiredSources?.includes(source)),
        )
        .map(graph => ({
          ...graph,
          type: compareMode ? "line" : graph.type,
          isPercent: graph.labelAggType
            ? ["percent-total", "percent-change", "percent-average"].includes(
                graph.labelAggType,
              )
            : false,
          ["compared.value"]: compareMode
            ? (aggFunc(
                typedMetrics,
                graph.dataKey,
                graph.labelAggType ?? "sum",
                graphs,
                comparedMetricPrefix,
                excludeDataKeyFromTotal,
                graph.yAxisId,
              ) ?? 0)
            : null,
          value:
            aggFunc(
              typedMetrics,
              graph.dataKey,
              graph.labelAggType ?? "sum",
              graphs,
              mainMetricPrefix,
              excludeDataKeyFromTotal,
              graph.yAxisId,
            ) ?? 0,
          ["compared.total"]:
            (compareMode &&
              (["percent-total", "percent-change", "percent-average"].includes(
                graph?.labelAggType ?? "",
              )
                ? aggFunc(
                    typedMetrics,
                    graph.dataKey,
                    "avg",
                    graphsProps,
                    comparedMetricPrefix,
                    excludeDataKeyFromTotal,
                    graph.yAxisId,
                  )
                : 0)) ||
            null,
          total: [
            "percent-total",
            "percent-change",
            "percent-average",
          ].includes(graph?.labelAggType ?? "")
            ? aggFunc(
                typedMetrics,
                graph.dataKey,
                graph.labelAggType ?? "sum",
                graphsProps,
                mainMetricPrefix,
                excludeDataKeyFromTotal,
                graph.yAxisId,
              )
            : 0,
        }))
        .sort((a, b) =>
          graphsManualSort ? 0 : b.dataKey.localeCompare(a.dataKey),
        ),
    [normalizedMetrics, graphs, focusedCharts],
  )

  const aggDataMap = useMemo(
    () => new Map(aggData.map((item, index) => [item.dataKey, index])),
    [aggData],
  )

  const sortFunction = useCallback(
    (a: TGraphProps, b: TGraphProps) => {
      if (graphsManualSort) {
        return 0
      }
      if (isMetricHighlighted(a.dataKey)) {
        return -1
      }
      if (isMetricHighlighted(b.dataKey)) {
        return 1
      }
      if (a.isTotal) {
        return 1
      }
      if (b.isTotal) {
        return -1
      }
      return (
        (aggDataMap.get(a.dataKey) ?? -1) - (aggDataMap.get(b.dataKey) ?? -1)
      )
    },
    [aggDataMap],
  )

  const sortedGraphs = useMemo(() => {
    if (!graphsManualSort) {
      const innerSortedGraphs = graphs
        .slice()
        .sort(sortFunction)
        .map((graph, idx) => ({
          ...graph,
          color: graph.color ?? getLightColor(getColorFromPaletteByIdx(idx)),
        }))
      return !isInverted ? innerSortedGraphs.reverse() : innerSortedGraphs
    }

    const innerGraphs = graphs.slice().map((graph, idx) => ({
      ...graph,
      color: graph.color ?? getLightColor(getColorFromPaletteByIdx(idx)),
    }))
    return !isInverted ? innerGraphs.reverse() : innerGraphs
  }, [graphs, aggData, isInverted])

  const getGeneratedLoadingData = () =>
    CHARTS_LOADING_DATA.map(item => {
      const dataItem: Record<string, unknown> = {
        ...item,
      }

      sortedGraphs.forEach(({ dataKey }) => {
        dataItem[dataKey] = Math.floor(Math.random() * 1000)
      })

      return dataItem
    })

  const loadingData = useChartLoadingDataForAnimation(
    isLoading,
    getGeneratedLoadingData,
  )

  const dataLabels = useMemo(
    () =>
      new Map(
        [...sortedGraphs].reverse().map((graph, idx) => [
          graph.dataKey,
          {
            label: graph.label ?? graph.dataKey,
            numberType: graph.numberType,
            unit: graph.unit,
            order: idx,
            isBrand: graph.isBrand,
            dataKey: graph.dataKey,
            pluralizeUnit: graph.pluralizeUnit ?? true,
          },
        ]),
      ),
    [sortedGraphs],
  )

  const yAxis = useMemo(() => {
    const axisMap = new Set(
      sortedGraphs.map(({ yAxisId }) => yAxisId).filter(id => id),
    )
    return Array.from(axisMap)
  }, [sortedGraphs])

  const toggleChartSelection = (source: string) => () => {
    // if no chart requires this source then do nothing
    if (
      !sortedGraphs.some(({ requiredSources }) =>
        requiredSources?.includes(source),
      )
    ) {
      return
    }
    setFocusedCharts(prevFocusedCharts => {
      if (prevFocusedCharts && prevFocusedCharts.includes(source)) {
        return prevFocusedCharts.filter(chartId => chartId !== source)
      }
      return prevFocusedCharts ? [...prevFocusedCharts, source] : [source]
    })
  }

  const aggregationGroups = useAggregationGroups(dataAggTypeOnly)

  const noData =
    normalizedMetrics?.length === 0 ||
    !normalizedMetrics?.some(entry =>
      sortedGraphs.some(({ dataKey }) => get(entry, dataKey) !== undefined),
    ) ||
    !normalizedMetrics?.some(entry =>
      sortedGraphs.some(({ dataKey }) => get(entry, dataKey) !== 0),
    )

  const handleAggregationGroup = (type: AggregationType) => {
    setDataAggType(type)
    setDataAggTypeProp(type)
  }

  const totalHeader = useMemo(() => {
    if (!totalComponent) {
      return null
    }
    return (
      <>
        {createElement(totalComponent, {
          metrics: normalizedMetrics,
          totalDataKey: totalLabelProps?.dataKey ?? TOTAL_CALCULATED_DATA_KEY,
          label: totalLabelProps?.label ?? "Total",
          skipKeysForTotal: totalLabelProps?.totalConfig?.skipMetrics,
          compareMode,
          isCompareMetricMain,
        })}
        {noData && emptyStateText}
      </>
    )
  }, [
    totalComponent,
    normalizedMetrics,
    totalLabelProps,
    compareMode,
    isCompareMetricMain,
    noData,
    emptyStateText,
    TOTAL_CALCULATED_DATA_KEY,
  ])

  const cartesianGridStyles = useMemo(
    () => ({ stroke: theme.colors["gray-dark"] }),
    [theme],
  )

  const yAxisStyle = useMemo(
    () => ({
      fontSize: `${theme.fontSize.sm}`,
      textAnchor: "end" as CSSProperties["textAnchor"],
      transform: "translate(-10px, 10px)",
    }),
    [theme],
  )

  const yAxisStyleLeft = useMemo(
    () => ({
      fontSize: `${theme.fontSize.sm}`,
      textAnchor: "start" as CSSProperties["textAnchor"],
      transform: "translate(10px, 10px)",
    }),
    [theme],
  )

  const handleChartClick = useCallback(() => {
    setTooltipFrozen(frozen => !frozen)
  }, [setTooltipFrozen])

  return (
    <div
      className={cn(
        "relative flex flex-col justify-between composed-chart pb-10",
        className,
      )}
    >
      <div>
        {(title || subTitle) && (
          <div className="mb-3 col-span-3">
            {title && (
              <div className="inline-flex items-center mb-system-xs gap-system-xs">
                <H3>{title}</H3>
                <div className="flex gap-1 items-center">
                  {description && (
                    <RichTooltip
                      content={
                        <Card
                          rounded
                          className="min-w-128 max-w-180 p-0 bg-transparent border-none"
                        >
                          {description}
                        </Card>
                      }
                    >
                      <InfoSvg
                        className="cursor-pointer"
                        size={20}
                        fill="white"
                      />
                    </RichTooltip>
                  )}
                </div>
              </div>
            )}
            {subTitle ? (
              <SubTitle className="font-bold max-h-16 text-gray-light inline ms-2">
                {subTitle}
              </SubTitle>
            ) : null}
            {totalHeader}
          </div>
        )}
        <div className="relative h-full">
          {dataSources.length > 0 ? (
            <div className="flex">
              {dataSources.map(source =>
                sources?.has(source) ? (
                  <Button
                    variant={{ variant: "solid", color: "blue" }}
                    key={source}
                    className={`w-8 h-8 ${interactive ? "pointer-events-auto" : "pointer-events-none"}`}
                    onClick={toggleChartSelection(source)}
                  >
                    <img
                      src={sources.get(source)?.icon}
                      alt={sources.get(source)?.name}
                      title={sources.get(source)?.name}
                      width={24}
                      height={24}
                      className={`object-cover ${
                        !focusedCharts.length || focusedCharts.includes(source)
                          ? "opacity-100"
                          : "opacity-50"
                      }`}
                    />
                  </Button>
                ) : null,
              )}
            </div>
          ) : null}
          <div className="join opacity-75 hover:opacity-100 absolute -bottom-9 right-0 z-40 h-9">
            {!hideAggSelector && aggregationGroups.length > 1
              ? aggregationGroups.map(({ type, long, short }) => (
                  <Button
                    variant={{ variant: "solid", color: "blue" }}
                    key={type}
                    title={long}
                    className={`join-item border text-3.5 px-4 py-3 rounded-sm ${type === (dataAggTypeOnly ?? dataAggType) ? "bg-basic-blue" : "bg-basic-dark-blue"}`}
                    onClick={() => handleAggregationGroup(type)}
                  >
                    {chartWidth < 550 ? short : long}
                  </Button>
                ))
              : null}
          </div>
        </div>
      </div>
      <div
        className="grow shrink-0 basis-auto relative"
        onClick={handleChartClick}
      >
        {isLoading && (
          <div className="flex items-center justify-center absolute top-0 bottom-0 right-0 left-0 bg-[#00000066] z-50">
            <Card rounded className="p-5">
              Loading ...
            </Card>
          </div>
        )}
        <ResponsiveContainer
          key={`chart-${chartWidth}-${size.height}-${noData ? "no-data" : ""}`}
          width="100%"
          minHeight={75}
          ref={wrapperRef}
          className="flex items-end grow shrink-0 basis-auto pb-system-3xs"
        >
          <ReComposedChart
            data={
              (isLoading || state !== "idle" || noData
                ? loadingData
                : normalizedMetrics) as Record<string, unknown>[]
            }
            margin={{
              top: sortedGraphs?.[0]?.labelAggType === "percent-total" ? 20 : 0,
              right: 0,
              bottom: 12,
              left: 0,
            }}
          >
            {normalizedMetrics
              .filter(({ date }) =>
                dayjs(date as string | number).isSame(
                  dayjs(date as string | number).startOf("quarter"),
                ),
              )
              .map(({ date }, index) => (
                <ReferenceLine
                  key={index}
                  x={date as string | number}
                  strokeDasharray="2 2"
                  yAxisId={yAxis?.[0]}
                  stroke="#FFFFFF44"
                  label={{
                    value: `Q${dayjs(date as string | number).quarter()}`,
                    position: "bottom",
                    dy: 10,
                    style: {
                      transform: "translateY(16px)",
                      fontSize: `${theme.fontSize.sm}`,
                      fill: "#FFFFFFAA",
                    },
                  }}
                />
              ))}
            <CartesianGrid
              strokeDasharray="3 0"
              vertical={false}
              style={cartesianGridStyles}
              stroke="#FFFFFF44"
            />
            <XAxis
              xAxisId={0}
              dataKey="date"
              tickFormatter={formatTick}
              interval="preserveStart"
              minTickGap={30}
              className="text-sm"
              fill="#FFFFFF44"
              tick={{ fill: "#FFFFFFAA" }}
            />
            <XAxis
              xAxisId={1}
              dataKey="comparedDate"
              tick={false}
              hide
              fill="#FFFFFF44"
            />
            {yAxis.map((id, idx) => (
              <YAxis
                domain={[
                  // all-positive data keeps the historical [0, max] domain;
                  // only genuinely negative values extend the axis below zero
                  id
                    ? Math.min(
                        0,
                        getMinValueByYAxisId(
                          id,
                          sortedGraphs,
                          normalizedMetrics as Record<string, number>[],
                        ),
                      )
                    : 0,
                  id
                    ? getMaxValueByYAxisId(
                        id,
                        sortedGraphs,
                        normalizedMetrics as Record<string, number>[],
                      )
                    : "dataMax",
                ]}
                orientation={idx ? "left" : "right"}
                interval="preserveEnd"
                style={idx ? yAxisStyleLeft : yAxisStyle}
                key={id}
                yAxisId={id}
                axisLine={false}
                tickLine={false}
                hide={idx > 1}
                width={0.01}
                allowDataOverflow
                tickFormatter={(value, index) =>
                  // hide the bottom tick label only when it is exactly 0 —
                  // a genuinely negative bottom tick must keep its label
                  index !== 0 || value !== 0
                    ? `${formatNumber(value, { compact: true })}`
                    : ""
                }
                tick={{ fill: "#FFFFFFAA" }}
              />
            ))}
            {yAxis.length < 1 && (
              <YAxis
                domain={[(dataMin: number) => Math.min(0, dataMin), "dataMax"]}
                orientation="right"
                interval="preserveEnd"
                padding={{ top: 0, bottom: 0 }}
                width={0.01}
                axisLine={false}
                tickLine={false}
                style={yAxisStyle}
                allowDataOverflow
                tickFormatter={(value, index) =>
                  // hide the bottom tick label only when it is exactly 0 —
                  // a genuinely negative bottom tick must keep its label
                  index !== 0 || value !== 0
                    ? `${formatNumber(value, { compact: true })}`
                    : ""
                }
                tick={{ fill: "#FFFFFFAA" }}
              />
            )}
            {sortedGraphs?.map(
              ({
                type = "line",
                dataKey,
                color,
                requiredSources,
                stacked: graphStacked,
                yAxisId,
              }) => {
                // hide chart if source is not connected
                if (
                  requiredSources &&
                  !requiredSources.some(source => sources?.has(source))
                ) {
                  return null
                  // hide chart if source is not focused
                } else if (
                  focusedCharts.length &&
                  requiredSources &&
                  !requiredSources?.some(source =>
                    focusedCharts.includes(source),
                  )
                ) {
                  return null
                }

                const mainDataKey = `${mainMetricPrefix}${dataKey}`

                switch (type) {
                  case "area":
                    return (
                      <Area
                        type="monotone"
                        key={mainDataKey}
                        dataKey={mainDataKey}
                        strokeWidth={2}
                        stroke={color}
                        fill={color}
                        yAxisId={yAxisId}
                        stackId={stacked || graphStacked ? 1 : undefined}
                        fillOpacity={
                          isMetricHighlighted(mainDataKey) ? 0.75 : 0.25
                        }
                        isAnimationActive={isAnimationActive}
                        animationDuration={1500}
                        animationBegin={0}
                      />
                    )
                  case "line":
                    return (
                      <Line
                        type="monotone"
                        key={mainDataKey}
                        dataKey={mainDataKey}
                        dot={{ r: 1 }}
                        strokeWidth={2}
                        stroke={color}
                        fill={color}
                        yAxisId={yAxisId}
                        fillOpacity={0.25}
                        isAnimationActive={isAnimationActive}
                        animationDuration={1500}
                        animationBegin={0}
                      />
                    )
                  case "dashed":
                    return (
                      <Line
                        type="monotone"
                        key={mainDataKey}
                        dataKey={mainDataKey}
                        dot={false}
                        strokeWidth={2}
                        strokeDasharray="6 6"
                        stroke={color}
                        fill={color}
                        yAxisId={yAxisId}
                        fillOpacity={0.25}
                        isAnimationActive={isAnimationActive}
                        animationDuration={1500}
                        animationBegin={0}
                      />
                    )
                  case "bar":
                    // fill MUST stay the plain graph color (no pattern url):
                    // the tooltip derives its color stripe from the main
                    // series fill. Multiple bar graphs auto-group side by
                    // side per time bucket.
                    return (
                      <Bar
                        key={mainDataKey}
                        dataKey={mainDataKey}
                        fill={color}
                        yAxisId={yAxisId}
                        activeBar={false}
                        isAnimationActive={isAnimationActive}
                        animationDuration={1500}
                        animationBegin={0}
                      />
                    )
                }
                return null
              },
            )}
            {compareMode &&
              sortedGraphs?.map(
                (
                  {
                    type = "line",
                    dataKey,
                    color,
                    requiredSources,
                    yAxisId,
                    stacked: graphStacked,
                  },
                  graphIdx,
                ) => {
                  // hide chart if source is not connected
                  if (
                    requiredSources &&
                    !requiredSources.some(source => sources?.has(source))
                  ) {
                    return null
                    // hide chart if source is not focused
                  } else if (
                    focusedCharts.length &&
                    requiredSources &&
                    !requiredSources?.some(source =>
                      focusedCharts.includes(source),
                    )
                  ) {
                    return null
                  }

                  const compareDataKey = `${comparedMetricPrefix}${dataKey}`

                  if (type === "bar") {
                    // comparison bars use a 45° hatched pattern in the
                    // graph's color (same precedent as BarChart.tsx)
                    const patternId = getComparePatternId(graphIdx)
                    return (
                      <Fragment key={compareDataKey}>
                        <defs>
                          <pattern
                            id={patternId}
                            width="5"
                            height="5"
                            patternUnits="userSpaceOnUse"
                            patternTransform="rotate(45)"
                          >
                            <line
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="11"
                              strokeWidth="2"
                              stroke={color}
                            />
                          </pattern>
                        </defs>
                        <Bar
                          dataKey={compareDataKey}
                          yAxisId={yAxisId}
                          fill={`url(#${patternId})`}
                          stroke={color}
                          activeBar={false}
                          isAnimationActive={isAnimationActive}
                          animationDuration={1500}
                          animationBegin={0}
                        />
                      </Fragment>
                    )
                  }

                  return (
                    <Area
                      type="monotone"
                      key={compareDataKey}
                      dataKey={compareDataKey}
                      dot={false}
                      yAxisId={yAxisId}
                      strokeWidth={2}
                      strokeDasharray="6 6"
                      stroke={color}
                      fill={color}
                      fillOpacity={0}
                      stackId={stacked || graphStacked ? 2 : undefined}
                      strokeOpacity={0.8}
                      isAnimationActive={isAnimationActive}
                      animationDuration={1500}
                      animationBegin={0}
                      z={2}
                    />
                  )
                },
              )}
            {state === "idle" && !noData ? (
              <Tooltip
                wrapperStyle={{ zIndex: 1000, pointerEvents: "auto" }}
                active={tooltipFrozen ? true : undefined}
                trigger={tooltipFrozen ? "click" : "hover"}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) {
                    return null
                  }
                  return (
                    <div
                      style={{
                        maxHeight: `${height ?? 300}px`,
                        overflowY: "auto",
                      }}
                    >
                      <ChartTooltipContent
                        title={title}
                        active={active}
                        payload={payload}
                        dataLabels={dataLabels}
                        isTotalCalculated={manualTotal}
                        showTotal={!hideTotal}
                        isCompareMetricMain={isCompareMetricMain}
                        compareMode={compareMode}
                        highlightKey={normalizedHighlightKey}
                        totalLabelConfig={totalLabelProps}
                        excludeDataKeyFromTotal={excludeDataKeyFromTotal}
                        tooltipProps={tooltipProps}
                        graphsManualSort={graphsManualSort}
                        blurTitle={blurTitle}
                        hideTooltipTitle={hideTooltipTitle}
                      >
                        {tooltipFooter}
                      </ChartTooltipContent>
                    </div>
                  )
                }}
              />
            ) : null}
          </ReComposedChart>
        </ResponsiveContainer>
      </div>
      {bottomLegend && (
        <div className="flex grow shrink-0 basis-auto">
          <VerticalLegend graphs={graphs} />

          {/* Chart Data Export Button */}
          {enableChartDataExport && (
            <div className="absolute bottom-0 right-0 z-40">
              <ChartDataExportButton
                data={normalizedMetrics}
                metrics={graphs.map(
                  graph => `${mainMetricPrefix}${graph.dataKey}`,
                )}
                dateKey={compareMode ? `${mainMetricPrefix}date` : undefined}
                compareMetrics={
                  compareMode
                    ? graphs.map(
                        graph => `${comparedMetricPrefix}${graph.dataKey}`,
                      )
                    : undefined
                }
                compareDateKey={
                  compareMode ? `${comparedMetricPrefix}date` : undefined
                }
                filename={chartDataExportFilename || `${title || "chart"}-data`}
                size="sm"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
